import React from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'core/modules/atoms/Icon/Icon';
import { stripHtml } from 'core/utils/utils';

import { CATEGORY_KEY_MAP } from '../../../constants/moderationMappings';
import { getUserIdFromStorage } from '../../../services/serviceConfig';
import { ANNOTATION_ITEM_TYPE, RADAR_ALERT_STATUS } from '../../../types/radar.constants';
import { formatDateOnly, getInitials } from '../../../utils/formatUtils';
import RadarTypeTag from '../RadarTypeTag/RadarTypeTag';
import SeverityBadge from '../SeverityIndicator/SeverityBadge';
import './_radar-card.scss';

import type { FlaggedMessageAnnotation, RadarAlert } from '../../../types/radar';

type RadarCardProps = {
  alert: RadarAlert;
  onClick?: (alert: RadarAlert) => void;
};

function RadarCard({ alert, onClick }: RadarCardProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  const isPending = alert.status === RADAR_ALERT_STATUS.PENDING;
  const isGroupAlert = alert.type === 'group-learning-alert';
  const isInappropriateUse = alert.type === 'inappropriate-use';
  const isAttended = alert.status === RADAR_ALERT_STATUS.REVIEWED || alert.status === RADAR_ALERT_STATUS.RESOLVED;
  const isDismissed = alert.status === RADAR_ALERT_STATUS.DISMISSED;
  const statusModifier = isAttended ? 'attended' : isDismissed ? 'dismissed' : 'pending';

  const flaggedCategories = isInappropriateUse && alert.raw.type === ANNOTATION_ITEM_TYPE.FLAGGED_MESSAGE ? (alert.raw as FlaggedMessageAnnotation).categories : [];

  const contentPrefix = isInappropriateUse ? t('chatbot:radar_content_prefix_message') : t('chatbot:radar_content_prefix_skill');

  const sanitizedLocations = alert.locations.map((loc) => stripHtml(loc)).filter(Boolean);

  const currentUserId = getUserIdFromStorage();
  const isReviewedByMe = alert.reviewedById && alert.reviewedById === currentUserId;
  const showReviewer = alert.reviewedById && isAttended;
  const showDismisser = alert.reviewedById && isDismissed;
  const reviewerDisplayName = isReviewedByMe ? t('chatbot:radar_reviewed_by_you') : (alert.reviewedByName ?? '');

  const hasFooter = sanitizedLocations.length > 0 || showReviewer || showDismisser;

  function handleClick(): void {
    onClick?.(alert);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(alert);
    }
  }

  const interactiveProps = onClick
    ? {
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        role: 'button' as const,
        tabIndex: 0 as const,
      }
    : undefined;

  return (
    <div className={`radar-card radar-card--${statusModifier}${!alert.viewed ? ' radar-card--unread' : ''}`} {...interactiveProps}>
      <div className="radar-card__content">
        <div className="radar-card__header">
          {isGroupAlert ? (
            <div className={`radar-card__avatar radar-card__avatar--group${!alert.viewed && isPending ? ' radar-card__avatar--unread' : ''}`}>
              <Icon type="students" size="default" color="white" />
            </div>
          ) : (
            <div className={`radar-card__avatar${!alert.viewed && isPending ? ' radar-card__avatar--unread' : ''}`}>
              {alert.avatar ? (
                <img alt={`${alert.studentName} ${alert.studentLastName}`} className="radar-card__avatar-img" src={alert.avatar} />
              ) : (
                getInitials(alert.studentName, alert.studentLastName)
              )}
            </div>
          )}
          <div className="radar-card__header-info">
            <div className="radar-card__name-row">
              <span className="radar-card__student-name">
                {isGroupAlert ? alert.groupName || t('chatbot:radar_group_label') : `${alert.studentName} ${alert.studentLastName}`}
              </span>
              <RadarTypeTag type={alert.type} />
              {flaggedCategories.map((cat) => {
                const translationKey = CATEGORY_KEY_MAP[cat.category];
                const label = translationKey ? t(`chatbot:${translationKey}`) : cat.category;
                return (
                  <span className="radar-card__category-badge" key={cat.category}>
                    {label}
                  </span>
                );
              })}
            </div>
            <span className="radar-card__subtitle">
              {isGroupAlert ? t('chatbot:radar_group_affected', { count: alert.affectedCount ?? 0 }) : alert.groupName}
            </span>
          </div>
        </div>

        <div className="radar-card__body">
          <div className="radar-card__body-left">
            <div className={`radar-card__bubble radar-card__bubble--${statusModifier}${isInappropriateUse ? ' radar-card__bubble--inappropriate' : ''}`}>
              <p className="radar-card__bubble-text">
                <span className="radar-card__content-prefix">{contentPrefix}</span> {alert.content}
              </p>
            </div>

            {hasFooter && (
              <div className="radar-card__footer">
                <div className="radar-card__footer-left">
                  {sanitizedLocations.length > 0 && <span className="radar-card__locations">{sanitizedLocations.join(' | ')}</span>}
                </div>
                <div className="radar-card__footer-right">
                  {showReviewer && (
                    <span className="radar-card__reviewer">
                      <span className="radar-card__reviewer-label">{t('chatbot:radar_reviewed_by')}</span>{' '}
                      <span className="radar-card__reviewer-name">{reviewerDisplayName}</span>
                    </span>
                  )}
                  {showDismisser && (
                    <span className="radar-card__reviewer">
                      <span className="radar-card__reviewer-label">{t('chatbot:radar_dismissed_by')}</span>{' '}
                      <span className="radar-card__reviewer-name">{reviewerDisplayName}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="radar-card__body-right">
            <SeverityBadge severity={alert.severity} />
            <span className="radar-card__date">{formatDateOnly(alert.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RadarCard;
